import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Linkedin, Loader2, RefreshCw, Copy, ExternalLink, CheckCircle2, Sparkles, Trash2, Zap, ArrowRight, PenLine } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { toast } from 'sonner';
import { ManualTaskDialog } from './linkedin/ManualTaskDialog';

export function LinkedInOutreachPanel({ leadId, leadName, linkedinUrl }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatingSeq, setGeneratingSeq] = useState(false);
  const [regeneratingId, setRegeneratingId] = useState(null);
  const [manualOpen, setManualOpen] = useState(false);
  const navigate = useNavigate();

  const loadTasks = useCallback(async () => {
    if (!leadId) return;
    setLoading(true);
    try {
      const res = await api.get(`/api/linkedin-tasks?lead_id=${leadId}`);
      setTasks(res.data?.tasks || []);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => { loadTasks(); }, [loadTasks]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await api.post(`/api/linkedin-tasks/${leadId}/generate`);
      // If the task already existed (returned existing), merge it
      setTasks(prev => {
        const exists = prev.find(t => t.id === res.data.id);
        if (exists) return prev;
        return [res.data, ...prev];
      });
      toast.success('LinkedIn outreach task generated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate task');
    } finally {
      setGenerating(false);
    }
  };

  const handleToggleStatus = async (taskId) => {
    try {
      const res = await api.put(`/api/linkedin-tasks/${taskId}/status`);
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: res.data.status } : t));
      toast.success(res.data.status === 'completed' ? 'Marked as sent' : 'Marked as pending');
    } catch {
      toast.error('Failed to update task');
    }
  };

  const handleRegenerate = async (taskId) => {
    setRegeneratingId(taskId);
    try {
      const res = await api.post(`/api/linkedin-tasks/${taskId}/regenerate`);
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, connection_message: res.data.connection_message } : t));
      toast.success('Message regenerated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to regenerate');
    } finally {
      setRegeneratingId(null);
    }
  };

  const handleDelete = async (taskId) => {
    try {
      await api.delete(`/api/linkedin-tasks/${taskId}`);
      setTasks(prev => prev.filter(t => t.id !== taskId));
      toast.success('Task deleted');
    } catch {
      toast.error('Failed to delete task');
    }
  };

  const copyMessage = (message) => {
    navigator.clipboard.writeText(message);
    toast.success('Message copied to clipboard');
  };

  const handleGenerateSequence = async () => {
    setGeneratingSeq(true);
    try {
      const res = await api.post(`/api/linkedin-tasks/${leadId}/sequence?steps=3`, {}, { timeout: 90000 });
      const newTasks = res.data?.tasks || [];
      setTasks(prev => [...newTasks, ...prev]);
      toast.success(`${newTasks.length}-step LinkedIn sequence created`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sequence generation failed');
    } finally {
      setGeneratingSeq(false);
    }
  };

  const pendingTasks = tasks.filter(t => t.status === 'pending' || t.status === 'scheduled');
  const completedTasks = tasks.filter(t => t.status === 'completed');

  return (
    <Card className="border-sky-500/20" data-testid="linkedin-outreach-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-1">
          <CardTitle className="text-sm font-semibold flex items-center gap-1.5 min-w-0">
            <Linkedin className="h-4 w-4 text-sky-500 shrink-0" />
            <span className="truncate">LinkedIn Outreach</span>
          </CardTitle>
          <div className="flex items-center gap-1 shrink-0">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={loadTasks}
              data-testid="linkedin-refresh-btn"
              title="Refresh"
            >
              <RefreshCw className="h-3.5 w-3.5" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs gap-1"
              onClick={() => setManualOpen(true)}
              data-testid="linkedin-manual-task-btn"
              title="Write your own message"
            >
              <PenLine className="h-3 w-3" /> Manual
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs gap-1"
              onClick={handleGenerateSequence}
              disabled={generatingSeq}
              data-testid="generate-linkedin-sequence-btn"
              title="Generate 3-step outreach sequence"
            >
              {generatingSeq ? <Loader2 className="h-3 w-3 animate-spin" /> : <Zap className="h-3 w-3" />}
              Sequence
            </Button>
            <Button
              size="icon"
              className="h-7 w-7 bg-sky-600 hover:bg-sky-700 text-white"
              onClick={handleGenerate}
              disabled={generating}
              data-testid="generate-linkedin-task-btn"
              title="Generate Connection Request"
            >
              {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? (
          <div className="text-xs text-muted-foreground text-center py-4" data-testid="linkedin-loading">Loading tasks...</div>
        ) : tasks.length === 0 ? (
          <div className="text-center py-4 space-y-2" data-testid="linkedin-empty-state">
            <p className="text-xs text-muted-foreground">No outreach tasks yet</p>
            <p className="text-xs text-muted-foreground">
              Generate an AI-crafted LinkedIn connection message for {leadName || 'this lead'}
            </p>
          </div>
        ) : (
          <>
            {pendingTasks.map(task => (
              <TaskCard
                key={task.id}
                task={task}
                onCopy={copyMessage}
                onToggle={handleToggleStatus}
                onRegenerate={handleRegenerate}
                onDelete={handleDelete}
                regenerating={regeneratingId === task.id}
              />
            ))}
            {completedTasks.length > 0 && (
              <div className="pt-2 border-t border-border">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-2">Completed</p>
                {completedTasks.map(task => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    onCopy={copyMessage}
                    onToggle={handleToggleStatus}
                    onRegenerate={handleRegenerate}
                    onDelete={handleDelete}
                    regenerating={regeneratingId === task.id}
                  />
                ))}
              </div>
            )}
          </>
        )}
        
        {/* View All Link */}
        {tasks.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            className="w-full h-7 text-xs text-muted-foreground hover:text-sky-500 gap-1"
            onClick={() => navigate('/linkedin-outbox')}
            data-testid="view-all-linkedin-btn"
          >
            View all in LinkedIn Outbox <ArrowRight className="h-3 w-3" />
          </Button>
        )}
      </CardContent>
      <ManualTaskDialog
        open={manualOpen}
        onOpenChange={setManualOpen}
        lead={{ id: leadId, name: leadName, linkedin_url: linkedinUrl }}
        onCreated={loadTasks}
      />
    </Card>
  );
}

function TaskCard({ task, onCopy, onToggle, onRegenerate, onDelete, regenerating }) {
  const isCompleted = task.status === 'completed';

  return (
    <div
      className={`border rounded-lg p-3 space-y-2 transition-colors ${
        isCompleted
          ? 'border-emerald-500/20 bg-emerald-50/30 dark:bg-emerald-950/10'
          : 'hover:border-sky-500/30'
      }`}
      data-testid={`linkedin-task-${task.id}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <Badge
            variant="outline"
            className={`text-[10px] shrink-0 ${
              isCompleted
                ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30'
                : 'bg-sky-500/10 text-sky-500 border-sky-500/30'
            }`}
            data-testid="linkedin-task-status"
          >
            {isCompleted ? 'Sent' : 'Pending'}
          </Badge>
          <span className="text-xs font-medium truncate">{task.lead_name}</span>
          {task.lead_title && (
            <span className="text-[10px] text-muted-foreground truncate hidden sm:inline">
              {task.lead_title}
            </span>
          )}
        </div>
        <div className="flex items-center gap-0.5 shrink-0">
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-red-500/70 hover:text-red-500"
            onClick={() => onDelete(task.id)}
            data-testid="linkedin-task-delete-btn"
            title="Delete"
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </div>

      {/* Connection Message */}
      <div className="bg-muted/50 rounded-md p-2.5" data-testid="linkedin-task-message">
        <p className="text-xs leading-relaxed text-foreground/90">{task.connection_message}</p>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="sm"
            className="h-6 text-[10px] px-2 gap-1"
            onClick={() => onCopy(task.connection_message)}
            data-testid="linkedin-copy-btn"
          >
            <Copy className="h-3 w-3" /> Copy
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-6 text-[10px] px-2 gap-1"
            onClick={() => onRegenerate(task.id)}
            disabled={regenerating}
            data-testid="linkedin-regenerate-btn"
          >
            {regenerating ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
            New
          </Button>
          {task.linkedin_url && (
            <Button
              variant="outline"
              size="sm"
              className="h-6 text-[10px] px-2 gap-1"
              onClick={() => window.open(task.linkedin_url, '_blank')}
              data-testid="linkedin-open-profile-btn"
            >
              <ExternalLink className="h-3 w-3" /> Profile
            </Button>
          )}
        </div>
        <Button
          variant={isCompleted ? 'ghost' : 'default'}
          size="sm"
          className={`h-6 text-[10px] px-2 gap-1 ${
            isCompleted ? '' : 'bg-sky-600 hover:bg-sky-700 text-white'
          }`}
          onClick={() => onToggle(task.id)}
          data-testid="linkedin-task-toggle-btn"
        >
          <CheckCircle2 className="h-3 w-3" />
          {isCompleted ? 'Undo' : 'Mark Sent'}
        </Button>
      </div>
    </div>
  );
}
